// Строка состояния: страница курсора и число страниц (примерно; точно - после сборки в Word), слова и знаки,
// рисунки/таблицы/формулы, автосохранение, результат проверки (E/W), масштаб. Счётчики - с задержкой (большие документы).
import { useEffect, useState } from 'react';
import type { RegistryApi } from '../../core/registry';
import { appStore, useApp } from '../../core/store';
import { useNumbering } from '../../core/editor/numberingPlugin';
import { runCommand } from '../../core/ctx';
import { useUi, uiStore } from '../../ui/uiState';
import { Icon } from '../../ui/icons';
import { setZoom, zoomStep, ZOOM_MAX, ZOOM_MIN } from './commands';

import { WORD } from '../../core/textStats';

/** Слова и знаки документа (пересчёт не чаще раза в 400 мс). */
function useTextStats() {
  const editor = useApp((s) => s.editor);
  const [st, setSt] = useState({ words: 0, chars: 0, charsNoSp: 0, selWords: 0 });
  useEffect(() => {
    if (!editor) return;
    let t = 0;
    const calc = () => {
      if (editor.isDestroyed) return;
      const { doc, selection } = editor.state;
      const text = doc.textBetween(0, doc.content.size, '\n', ' ');
      const words = (text.match(WORD) || []).length;
      const chars = text.replace(/\n/g, '').length;
      const charsNoSp = text.replace(/\s/g, '').length;
      const selWords = selection.empty ? 0 : (doc.textBetween(selection.from, selection.to, ' ', ' ').match(WORD) || []).length;
      setSt({ words, chars, charsNoSp, selWords });
    };
    const on = () => { window.clearTimeout(t); t = window.setTimeout(calc, 400); };
    calc();
    editor.on('update', on);
    editor.on('selectionUpdate', on);
    return () => { window.clearTimeout(t); editor.off('update', on); editor.off('selectionUpdate', on); };
  }, [editor]);
  return st;
}

function PageStatus() {
  const pages = useUi((s) => s.pages);
  const built = useUi((s) => s.built);
  const rev = useApp((s) => s.revision);
  const exact = built?.pages && built.revision === rev ? built.pages : null;
  const total = exact || pages.total;
  if (!total) return null;
  return (
    <button type="button" className="status-item status-btn" data-testid="status-page" onClick={() => runCommand('view.preview')}
      data-tip={exact ? 'Число страниц по последней сборке в Word' : 'Примерно; точно - после сборки PDF (щелчок - предпросмотр)'}>
      <Icon name="file" size={13} /> Страница {Math.min(pages.current || pages.front + 1, total)} из {exact ? total : '≈' + total}
    </button>
  );
}

function TextStatus() {
  const st = useTextStats();
  return (
    <span className="status-item" data-testid="status-words" data-tip="Знаков с пробелами / без пробелов"
      data-tip-desc={`${st.chars.toLocaleString('ru-RU')} / ${st.charsNoSp.toLocaleString('ru-RU')}`}>
      {st.selWords ? `${st.selWords} из ` : ''}{st.words.toLocaleString('ru-RU')} {plural(st.words, 'слово', 'слова', 'слов')}
      <span className="status-dim"> · {st.chars.toLocaleString('ru-RU')} зн.</span>
    </span>
  );
}

function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10; const m100 = n % 100;
  return m10 === 1 && m100 !== 11 ? one : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? few : many;
}

function ObjectsStatus() {
  const nb = useNumbering();
  const c = nb.counts;
  const open = () => { const p = appStore.getState().panels; appStore.getState().set({ panels: { ...p, left: 'structure' } }); };
  return (
    <button type="button" className="status-item status-btn" data-testid="status-counts" onClick={open} data-tip="Открыть структуру документа"
      data-tip-desc={`Разделов: ${c.sections}, листингов: ${c.listings}${c.appendices ? `, приложений: ${c.appendices}` : ''}`}>
      <span><Icon name="image" size={13} /> {c.figures}</span>
      <span><Icon name="table" size={13} /> {c.tables}</span>
      <span><Icon name="formula" size={13} /> {c.equations}</span>
    </button>
  );
}

const SAVE_TEXT: Record<string, string> = { saved: 'Сохранено', dirty: 'Изменено', saving: 'Сохранение…', error: 'Не сохранено', conflict: 'Конфликт' };
function SaveStatus() {
  const save = useApp((s) => s.save);
  const t = save.at ? new Date(save.at) : null;
  const time = t && !isNaN(+t) ? t.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
  const icon = save.state === 'saved' ? 'ok' : save.state === 'saving' || save.state === 'dirty' ? 'spinner' : 'offline';
  return (
    <span className={'status-item status-save save-' + save.state} data-testid="status-save"
      data-tip={save.state === 'saved' ? 'Документ сохраняется сам через секунду после правки' : SAVE_TEXT[save.state]}
      data-tip-desc={save.error || (time ? `Последнее сохранение: ${time}` : undefined)}>
      <Icon name={icon} size={13} className={save.state === 'saving' ? 'spin' : ''} />
      {SAVE_TEXT[save.state]}{save.state === 'saved' && time ? ` в ${time}` : ''}
    </span>
  );
}

/** Итог последней полной проверки по СТО (живые подсказки показывает функция sto_intel). */
function ZoomStatus() {
  const zoom = useApp((s) => s.zoom);
  const mode = useUi((s) => s.zoomMode);
  const toPos = (z: number) => Math.round((Math.log(z / ZOOM_MIN) / Math.log(ZOOM_MAX / ZOOM_MIN)) * 100);
  const fromPos = (p: number) => ZOOM_MIN * Math.pow(ZOOM_MAX / ZOOM_MIN, p / 100);
  return (
    <span className="status-zoom" data-testid="status-zoom">
      <button type="button" className={'zoom-fit' + (mode === 'fit' ? ' is-active' : '')} onClick={() => runCommand('view.zoomFit')} data-tip="По ширине">
        <Icon name="maximize" size={13} /></button>
      <button type="button" onClick={() => zoomStep(-1)} aria-label="Мельче" data-tip="Мельче" data-tip-key="Ctrl+−">−</button>
      <input type="range" min={0} max={100} value={toPos(zoom)} aria-label="Масштаб"
        onChange={(e) => { const z = fromPos(Number(e.target.value)); setZoom(Math.abs(z - 1) < 0.04 ? 1 : z); }} />
      <button type="button" onClick={() => zoomStep(1)} aria-label="Крупнее" data-tip="Крупнее" data-tip-key="Ctrl+=">+</button>
      <button type="button" className="zoom-val" onClick={() => setZoom(1)} data-tip="Масштаб 100 %" data-tip-key="Ctrl+0">{Math.round(zoom * 100)} %</button>
    </span>
  );
}

function FocusExit() {
  const focus = useUi((s) => s.focusMode);
  if (!focus) return null;
  return <button type="button" className="status-item status-btn" onClick={() => uiStore.getState().set({ focusMode: false })}><Icon name="focus" size={13} /> Выйти из «Фокуса» (Esc)</button>;
}

export function setupStatus(r: RegistryApi) {
  r.statusbar.add({ id: 'page', side: 'left', order: 5, component: PageStatus });
  r.statusbar.add({ id: 'words', side: 'left', order: 10, component: TextStatus });
  r.statusbar.add({ id: 'counts', side: 'left', order: 20, component: ObjectsStatus });
  // итог проверки показывает элемент 'sto.status' (sto_intel): живая проверка + полная проверка + её ход
  r.statusbar.add({ id: 'focus', side: 'right', order: 5, component: FocusExit });
  r.statusbar.add({ id: 'save', side: 'right', order: 10, component: SaveStatus });
  r.statusbar.add({ id: 'zoom', side: 'right', order: 90, component: ZoomStatus });
}

